import * as THREE from 'three';
import { TERRAIN_RENDER } from '../config/tuning';
import { TERRAIN_FRAGMENT, TERRAIN_VERTEX } from '../render/shaders/terrain.glsl';
import { createDetailTexture } from './generators/Textures';
import type { Sky } from './Sky';
import type { Terrain, TerrainBounds } from './Terrain';

/**
 * Terrain rendering: CDLOD quadtree selection on the CPU, geomorphing on the GPU.
 *
 * The whole theatre is one instanced draw call. Each frame the quadtree is walked, patches are
 * selected by distance and frustum, and their origin/size/level go into an instance attribute;
 * the vertex shader does the rest. Nothing is remeshed, so flying at 90 m/s costs the same as
 * hovering.
 *
 * Heights live in a half-float texture holding *normalised* height. Half-float precision is
 * relative, so this is exact to a couple of centimetres near sea level — where the beach and
 * the waterline are — and about half a metre at the peaks, where nothing can tell.
 */

const patchBounds = new THREE.Box3();
const patchCentre = new THREE.Vector3();
const cameraScratch = new THREE.Vector3();

export class TerrainRenderer {
  readonly object3d: THREE.Mesh;

  /** Patches drawn last frame. Reported by the debug overlay. */
  patchCount = 0;

  /** The heightfield texture. Shared with the ocean, which needs the same bathymetry. */
  get heights(): THREE.DataTexture {
    return this.heightTexture;
  }

  private readonly terrain: Terrain;
  private readonly bounds: TerrainBounds[];
  private readonly geometry: THREE.InstancedBufferGeometry;
  private readonly material: THREE.ShaderMaterial;
  private readonly heightTexture: THREE.DataTexture;
  private readonly detailTexture: THREE.DataTexture;
  private readonly patchAttribute: THREE.InstancedBufferAttribute;
  private readonly patchData: Float32Array;
  private readonly lodRangesM: number[] = [];
  private readonly frustum = new THREE.Frustum();
  private readonly viewProjection = new THREE.Matrix4();

  constructor(terrain: Terrain, sky: Sky) {
    this.terrain = terrain;
    this.bounds = terrain.buildBoundsPyramid(TERRAIN_RENDER.lodLevels + 2);

    for (let level = 0; level <= TERRAIN_RENDER.lodLevels; level += 1) {
      this.lodRangesM.push(TERRAIN_RENDER.baseLodRangeM * 2 ** level);
    }

    this.heightTexture = createHeightTexture(terrain);
    this.detailTexture = createDetailTexture();

    this.geometry = createPatchGeometry(TERRAIN_RENDER.patchVertices);
    this.patchData = new Float32Array(TERRAIN_RENDER.maxPatches * 4);
    this.patchAttribute = new THREE.InstancedBufferAttribute(this.patchData, 4);
    this.patchAttribute.setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute('aPatch', this.patchAttribute);

    const heightSpan = terrain.maxHeightM - terrain.minHeightM;
    const morphRanges: THREE.Vector2[] = [];
    for (let level = 0; level < 8; level += 1) {
      const range = this.lodRangesM[Math.min(level, this.lodRangesM.length - 1)];
      morphRanges.push(new THREE.Vector2(range * TERRAIN_RENDER.morphStartRatio, range));
    }

    this.material = new THREE.ShaderMaterial({
      vertexShader: TERRAIN_VERTEX,
      fragmentShader: TERRAIN_FRAGMENT,
      uniforms: {
        uHeightMap: { value: this.heightTexture },
        uDetailMap: { value: this.detailTexture },
        uWorldOrigin: { value: new THREE.Vector2(-terrain.worldSizeM / 2, -terrain.worldSizeM / 2) },
        uWorldSize: { value: terrain.worldSizeM },
        uHeightScale: { value: heightSpan },
        uHeightOffset: { value: terrain.minHeightM },
        uGridDim: { value: TERRAIN_RENDER.patchVertices - 1 },
        uCameraPosition: { value: new THREE.Vector3() },
        uMorphRanges: { value: morphRanges },
        uTexelWorldM: { value: terrain.sampleSpacingM },
        uSunDirection: { value: sky.sunDirection },
        uSunColour: { value: new THREE.Color() },
        uAmbientSky: { value: new THREE.Color() },
        uAmbientGround: { value: new THREE.Color(0.05, 0.06, 0.06) },
        uRockColour: { value: new THREE.Color(0.33, 0.31, 0.28) },
        uScrubColour: { value: new THREE.Color(0.24, 0.29, 0.18) },
        uSandColour: { value: new THREE.Color(0.62, 0.57, 0.44) },
        uHighColour: { value: new THREE.Color(0.46, 0.45, 0.43) },
        uDetailNearScale: { value: 1 / TERRAIN_RENDER.detailNearM },
        uDetailFarScale: { value: 1 / TERRAIN_RENDER.detailFarM },
        uHazeColour: { value: new THREE.Color() },
        uHazeDensity: { value: sky.hazeDensity },
      },
    });

    this.object3d = new THREE.Mesh(this.geometry, this.material);
    // The patch mesh is a unit square; its real extent comes from per-instance uniforms, so the
    // usual bounding-volume culling cannot see it. Selection already culls against the frustum.
    this.object3d.frustumCulled = false;
    this.object3d.renderOrder = 0;
  }

  /**
   * Choose patches for this frame. `camera` must already have its world matrix updated — the
   * frustum is taken from it, and a stale matrix shows up as terrain popping in at the edges.
   */
  update(camera: THREE.PerspectiveCamera, sky: Sky): void {
    cameraScratch.copy(camera.position);
    this.material.uniforms['uCameraPosition'].value.copy(cameraScratch);
    (this.material.uniforms['uSunColour'].value as THREE.Color).copy(sky.sunColour);
    (this.material.uniforms['uAmbientSky'].value as THREE.Color)
      .copy(sky.horizonColour)
      .multiplyScalar(0.55);
    (this.material.uniforms['uHazeColour'].value as THREE.Color).copy(sky.hazeColour);
    this.material.uniforms['uHazeDensity'].value = sky.hazeDensity;

    this.viewProjection.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.viewProjection);

    this.patchCount = 0;
    const half = this.terrain.worldSizeM / 2;
    const rootsPerSide = Math.round(this.terrain.worldSizeM / TERRAIN_RENDER.rootChunkM);
    const rootSize = this.terrain.worldSizeM / rootsPerSide;

    for (let rz = 0; rz < rootsPerSide; rz += 1) {
      for (let rx = 0; rx < rootsPerSide; rx += 1) {
        this.selectPatch(-half + rx * rootSize, -half + rz * rootSize, rootSize, TERRAIN_RENDER.lodLevels);
      }
    }

    this.geometry.instanceCount = this.patchCount;
    this.patchAttribute.needsUpdate = true;
  }

  /**
   * Recursive CDLOD selection. A node is emitted at its own level when the camera is beyond the
   * range of the *next finer* level; otherwise it subdivides. The finest level always emits.
   */
  private selectPatch(originX: number, originZ: number, size: number, level: number): void {
    if (this.patchCount >= TERRAIN_RENDER.maxPatches) return;

    const nodeBounds = this.boundsFor(originX, originZ, size);
    patchBounds.min.set(originX, nodeBounds.min, originZ);
    patchBounds.max.set(originX + size, nodeBounds.max, originZ + size);
    if (!this.frustum.intersectsBox(patchBounds)) return;

    if (level > 0) {
      const finerRange = this.lodRangesM[level - 1];
      const distance = distanceToBox(cameraScratch, patchBounds, patchCentre);
      if (distance < finerRange) {
        const half = size / 2;
        this.selectPatch(originX, originZ, half, level - 1);
        this.selectPatch(originX + half, originZ, half, level - 1);
        this.selectPatch(originX, originZ + half, half, level - 1);
        this.selectPatch(originX + half, originZ + half, half, level - 1);
        return;
      }
    }

    const offset = this.patchCount * 4;
    this.patchData[offset] = originX;
    this.patchData[offset + 1] = originZ;
    this.patchData[offset + 2] = size;
    this.patchData[offset + 3] = level;
    this.patchCount += 1;
  }

  /** Height range of the node covering this square, from the precomputed pyramid. */
  private boundsFor(originX: number, originZ: number, size: number): { min: number; max: number } {
    const nodesPerSide = Math.round(this.terrain.worldSizeM / size);
    let level = 0;
    for (; level < this.bounds.length; level += 1) {
      if (this.bounds[level].nodesPerSide <= nodesPerSide) break;
    }
    const layer = this.bounds[Math.min(level, this.bounds.length - 1)];

    const half = this.terrain.worldSizeM / 2;
    const scale = layer.nodesPerSide / this.terrain.worldSizeM;
    const x0 = Math.max(0, Math.floor((originX + half) * scale));
    const z0 = Math.max(0, Math.floor((originZ + half) * scale));
    const x1 = Math.min(layer.nodesPerSide - 1, Math.ceil((originX + size + half) * scale) - 1);
    const z1 = Math.min(layer.nodesPerSide - 1, Math.ceil((originZ + size + half) * scale) - 1);

    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    for (let z = z0; z <= z1; z += 1) {
      for (let x = x0; x <= x1; x += 1) {
        const index = z * layer.nodesPerSide + x;
        if (layer.min[index] < min) min = layer.min[index];
        if (layer.max[index] > max) max = layer.max[index];
      }
    }
    if (!Number.isFinite(min)) return { min: this.terrain.minHeightM, max: this.terrain.maxHeightM };
    return { min, max };
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
    this.heightTexture.dispose();
    this.detailTexture.dispose();
  }
}

/** Shortest distance from a point to an axis-aligned box, metres. */
function distanceToBox(point: THREE.Vector3, box: THREE.Box3, scratch: THREE.Vector3): number {
  scratch.set(
    Math.max(box.min.x, Math.min(point.x, box.max.x)),
    Math.max(box.min.y, Math.min(point.y, box.max.y)),
    Math.max(box.min.z, Math.min(point.z, box.max.z)),
  );
  return scratch.distanceTo(point);
}

/**
 * The shared unit grid, in the XZ plane, spanning 0..1. Built once; every patch in the theatre is
 * an instance of it.
 */
function createPatchGeometry(verticesPerSide: number): THREE.InstancedBufferGeometry {
  const cells = verticesPerSide - 1;
  const positions = new Float32Array(verticesPerSide * verticesPerSide * 3);
  for (let z = 0; z < verticesPerSide; z += 1) {
    for (let x = 0; x < verticesPerSide; x += 1) {
      const index = (z * verticesPerSide + x) * 3;
      positions[index] = x / cells;
      positions[index + 1] = 0;
      positions[index + 2] = z / cells;
    }
  }

  const indices = new Uint32Array(cells * cells * 6);
  let cursor = 0;
  for (let z = 0; z < cells; z += 1) {
    for (let x = 0; x < cells; x += 1) {
      const a = z * verticesPerSide + x;
      const b = a + 1;
      const c = a + verticesPerSide;
      const d = c + 1;
      indices[cursor] = a;
      indices[cursor + 1] = c;
      indices[cursor + 2] = b;
      indices[cursor + 3] = b;
      indices[cursor + 4] = c;
      indices[cursor + 5] = d;
      cursor += 6;
    }
  }

  const geometry = new THREE.InstancedBufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  return geometry;
}

/** Upload the heightfield as a normalised half-float texture for vertex-shader sampling. */
function createHeightTexture(terrain: Terrain): THREE.DataTexture {
  const { resolution, heights, minHeightM, maxHeightM } = terrain;
  const span = maxHeightM - minHeightM;
  const data = new Uint16Array(resolution * resolution);
  for (let i = 0; i < data.length; i += 1) {
    data[i] = THREE.DataUtils.toHalfFloat((heights[i] - minHeightM) / span);
  }

  const texture = new THREE.DataTexture(
    data,
    resolution,
    resolution,
    THREE.RedFormat,
    THREE.HalfFloatType,
  );
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}
